/**
 * Assigned Labs component for learner's My Labs page
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { assignmentService, labService, runService, webSocketService } from '../../services';
import { Card, Badge, Button, Alert, LoadingSpinner } from '../ui';
import type { Lab } from '../../types';

interface AssignedLabsProps {
  onLabLaunch?: (labSlug: string) => void;
}

export function AssignedLabs({ onLabLaunch }: AssignedLabsProps) {
  const navigate = useNavigate();
  const [assignedLabs, setAssignedLabs] = useState<Lab[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [launchLoading, setLaunchLoading] = useState<Record<string, boolean>>({});

  const loadAssignedLabs = async () => {
    try {
      setError(null);
      const labs = await assignmentService.getMyAssignedLabs();
      setAssignedLabs(Array.isArray(labs) ? labs : []);
    } catch (err: any) {
      console.error('Failed to load assigned labs:', err);
      setError('Failed to load assigned labs. Please try again.');
      setAssignedLabs([]); // Ensure it's always an array
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssignedLabs();
  }, []);

  // WebSocket listeners for assignment updates
  useEffect(() => {
    const unsubscribeCompleted = webSocketService.onAssignmentCompleted(() => {
      // Show notification
      setError(null);
      // Refresh assigned labs
      loadAssignedLabs();
    });

    const unsubscribeCreated = webSocketService.onAssignmentCreated(() => {
      // Show notification
      setError(null);
      // Refresh assigned labs
      loadAssignedLabs();
    });

    return () => {
      unsubscribeCompleted();
      unsubscribeCreated();
    };
  }, []);

  const handleLaunchLab = async (labSlug: string) => {
    setLaunchLoading(prev => ({ ...prev, [labSlug]: true }));
    
    try {
      // Create a new run for the assigned lab
      await runService.createRun({ lab_id: labSlug });
      onLabLaunch?.(labSlug);
      
      // Optionally navigate to runs or show success message
      // For now, we'll just call the callback
    } catch (err: any) {
      console.error('Failed to launch assigned lab:', err);
      setError(`Failed to launch ${labSlug}: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLaunchLoading(prev => ({ ...prev, [labSlug]: false }));
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return 'bg-green-500';
      case 'intermediate':
        return 'bg-yellow-500';
      case 'advanced':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <Card className="bg-gray-800 border-gray-700">
        <div className="flex items-center justify-center p-8">
          <LoadingSpinner size="md" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-gray-800 border-gray-700">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white" data-testid="assigned-labs-section">
            Assigned Labs ({assignedLabs ? assignedLabs.length : 0})
          </h2>
          {assignedLabs && assignedLabs.length > 0 && (
            <span className="text-sm text-gray-400">
              Labs assigned by your instructor
            </span>
          )}
        </div>

        {error && (
          <Alert variant="error" className="mb-4">
            {error}
            <Button 
              onClick={() => setError(null)} 
              className="ml-4" 
              size="sm" 
              variant="secondary"
            >
              Dismiss
            </Button>
          </Alert>
        )}

        {!assignedLabs || assignedLabs.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400 mb-2">No labs have been assigned to you yet.</p>
            <p className="text-sm text-gray-500">
              Check the Lab Catalog below for self-paced labs you can start anytime.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assignedLabs.map((lab) => (
              <Card key={lab.slug} className="bg-gray-700 border-gray-600" data-testid={`assigned-lab-${lab.slug}`}>
                <div className="space-y-3">
                  {/* Lab Header */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Badge className={`${getDifficultyColor(lab.difficulty)} text-white text-xs`}>
                        {lab.difficulty}
                      </Badge>
                      <Badge variant="info" size="sm">
                        ASSIGNED
                      </Badge>
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-1">
                      {lab.title}
                    </h3>
                    {lab.summary && (
                      <p className="text-sm text-gray-300 line-clamp-2">
                        {lab.summary}
                      </p>
                    )}
                  </div>

                  {/* Lab Details */}
                  <div className="space-y-2">
                    <div className="text-sm text-gray-400">
                      <span>Estimated Time:</span>
                      <span className="ml-2 text-white">{lab.estimated_minutes} min</span>
                    </div>
                    
                    {lab.tags && lab.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {lab.tags.slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="default" size="sm">
                            {tag}
                          </Badge>
                        ))}
                        {lab.tags.length > 3 && (
                          <Badge variant="default" size="sm">
                            +{lab.tags.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action Button */}
                  <div className="pt-3 border-t border-gray-600">
                    <Button
                      onClick={() => handleLaunchLab(lab.slug)}
                      disabled={launchLoading[lab.slug]}
                      className="w-full"
                      variant="primary"
                    >
                      {launchLoading[lab.slug] ? (
                        <>
                          <LoadingSpinner size="sm" className="mr-2" />
                          Launching...
                        </>
                      ) : (
                        'Start Assigned Lab'
                      )}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {assignedLabs && assignedLabs.length > 0 && (
          <div className="text-sm text-gray-500 border-t border-gray-700 pt-4">
            <p>
              💡 <strong>Tip:</strong> Assigned labs are automatically removed when you achieve a 100% grade.
              Complete all tasks carefully to earn full points!
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}
